import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import {
  emailValidator,
  MobileNoIntValidator,
  requiredValidator,
  skipValidator,
} from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import RippleButton from "../../components/common/RippleButton";
import ShadowCard from "../../components/common/ShadowCard";
import {
  createFlightInquiry,
  createHotelInquiry,
  createPackageInquiry,
  createVisaInquiry,
  getFlightInquiryByID,
  getHotelInquiryByID,
  getInquiryByID,
  getPackageInquiryByID,
  getVisaInquiryByID,
  updateFlightInquiry,
  updateHotelInquiry,
  updatePackageInquiry,
  updateVisaInquiry,
} from "./services/inquiry.services";
import { clearInquiryDetails } from "./inquirySlice";
import ButtonForAll from "../../components/common/ButtonForAll";
import { getAllActiveLeadSource } from "../leadsource/services/leadsource.service";
import { ILeadSource } from "../leadsource/leadsourceModel";
import FormDatePicker from "../../components/formFields/FormDateField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import {
  HOTELPREFERENCE,
  INQUIRYQUERIES,
  INQUIRYTYPE,
  NAMEPREFIX,
  PAXCOUNT,
  QUERYTYPES,
  TRIPTYPE,
} from "../../_contstants/common";
import moment from "moment";
import { getLeadByID } from "../lead/services/lead.services";
import FormRadioGroup from "../../components/formFields/FormRadioGroup";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import { getAllActiveCities } from "../city/services/city.services";
import FormCheckboxGroup from "../../components/formFields/FormCheckboxGroup";
import { getAllActiveFoodOptions } from "../foodoption/services/foodOption.services";
import { IFoodOption } from "../foodoption/foodOptionModel";
import { IAirlineClass } from "../airlineclass/airlineclassModel";
import { IAirline } from "../airline/airlineModel";
import { getAllActiveAirlines } from "../airline/services/airline.services";
import { getAllActiveAirlineClass } from "../airlineclass/services/airlineclass.services";
import { clearLeadDetails } from "../lead/leadSlice";
import {
  getContactByID,
  insertContactFromOtherPlace,
} from "../contact/services/contact.services";
import { getAllActiveSectors } from "../sector/services/sector.services";
import { ISector } from "../sector/sectorModel";
import { clearContactDetails } from "../contact/contactSlice";
import { ErrorToast } from "../../components/toast/Toasts";
import { getAllActiveVisaCategories } from "../visacategory/services/visa.services";
import { IVisaCategory } from "../visacategory/visaCategoryModel";
import { INationality } from "../nationality/nationalityModel";
import { getAllActiveNationalities } from "../nationality/services/nationality.services";
import FormRatingField from "../../components/formFields/FormRatingField";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import AppDialog from "../../components/dialog/Dialog";
import TravellerHotelRoomArray from "./TravellerHotelRoomArray";
import { openDialog } from "../../components/dialog/dialogSlice";
import FormIncrementalSearch from "../../components/formFields/FormIncrementalSearch";
import { getAllFlightPlaceBySearch } from "../flightplace/services/flightplace.services";
import { ICity } from "../city/cityModel";
import { getAllActiveVisaCountries } from "../visacountry/services/visacountry.services";
import { IVisaCountry } from "../visacountry/visacountryModel";
import { getAllActiveEntryTypes } from "../visaentrytype/services/visaentrytype.services";
import { IEntryType } from "../visaentrytype/visaentrytypeModel";
import { getAllActiveMealTypes } from "../mealtype/services/mealtype.services";
import { IMealType } from "../mealtype/mealtypeModel";
import { parseQueryParams } from "../../_helper/helper";

interface ChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const CheckinCheckoutChangeWatcher: React.FC<ChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const location = useLocation();
  const NationalityList = useAppSelector(
    (state) => state.nationality.NationalityList,
  );
  const InquiryID = location.state?.InquiryID;
  const CheckIn = formRenderProps.valueGetter("CheckIn");
  const CheckOut = formRenderProps.valueGetter("CheckOut");
  const RequirementType = formRenderProps.valueGetter("RequirementType");

  useEffect(() => {
    if (RequirementType === "HOTEL") {
      if (CheckIn && CheckOut) {
        const checkInDate = moment(CheckIn);
        const checkOutDate = moment(CheckOut);

        // Calculate difference in days
        const noOfNights = Math.max(0, checkOutDate.diff(checkInDate, "days"));

        formRenderProps.onChange("NoOfNights", {
          value: noOfNights,
        });
      } else {
        formRenderProps.onChange("NoOfNights", {
          value: 0,
        });
      }
    }
  }, [CheckIn, CheckOut]);

  useEffect(() => {
    if (!InquiryID && NationalityList?.length > 0) {
      const matchingIDs = NationalityList?.filter((item) =>
        item?.Nationality?.toLowerCase()?.includes("india"),
      )?.map((item) => item.ID);

      if (matchingIDs?.length > 0) {
        formRenderProps.onChange("NationalityID", { value: matchingIDs[0] });
      }
    }
  }, [NationalityList]);

  return null;
};

const CreateInquiry: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const InquiryID = location.state?.InquiryID;
  const RequirementType = location.state?.RequirementType;
  const LeadID = location.state?.LeadID;
  const ContactID = location.state?.ContactID;

  const searchParams = parseQueryParams(location.search);

  const loading = useAppSelector((state) => state.inquiry.loading);
  const contactLoading = useAppSelector((state) => state.contact.formLoading);
  const InquiryDetails = useAppSelector(
    (state) => state.inquiry.InquiryDetails,
  );
  const InquiryPackageDetails = useAppSelector(
    (state) => state.inquiry.InquiryPackageDetails,
  );
  const InquiryVisaDetails = useAppSelector(
    (state) => state.inquiry.InquiryVisaDetails,
  );
  const InquiryHotelDetails = useAppSelector(
    (state) => state.inquiry.InquiryHotelDetails,
  );
  const InquiryFlightDetails = useAppSelector(
    (state) => state.inquiry.InquiryFlightDetails,
  );
  const LeadDetails = useAppSelector((state) => state.lead.LeadDetails);
  const leadLoading = useAppSelector((state) => state.lead.formLoading);
  const ContactDetails = useAppSelector(
    (state) => state.contact.ContactDetails,
  );
  const LeadSourceList = useAppSelector(
    (state) => state.leadsource.LeadSourceList,
  );
  const VisaCountryList = useAppSelector(
    (state) => state.visacountry.VisaCountryList,
  );
  const VisaCategoryList = useAppSelector(
    (state) => state.visacategory.VisaCategoryList,
  );
  const NationalityList = useAppSelector(
    (state) => state.nationality.NationalityList,
  );
  const CityList = useAppSelector((state) => state.city.CityList);
  const SectorList = useAppSelector((state) => state.sector.SectorList);
  const AirlineList = useAppSelector((state) => state.airline.AirlineList);
  const AirlineClassList = useAppSelector(
    (state) => state.airlineclass.AirlineClassList,
  );
  const FoodOptionList = useAppSelector(
    (state) => state.foodOption.FoodOptionList,
  );
  const MealTypeList = useAppSelector((state) => state.mealType.MealTypeList);
  const EntryTypeList = useAppSelector(
    (state) => state.visaentrytype.EntryTypeList,
  );

  const [formKey, setFormKey] = useState(1);
  const [initialValues, setInitialValues] = useState<any>({});

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [initialValues]);

  useEffect(() => {
    if (
      RequirementType === "FLIGHT" ||
      searchParams?.RequirementType === "FLIGHT"
    ) {
      setInitialValues(InquiryFlightDetails);
    } else if (
      RequirementType === "PACKAGE" ||
      searchParams?.RequirementType === "PACKAGE"
    ) {
      setInitialValues(InquiryPackageDetails);
    } else if (
      RequirementType === "VISA" ||
      searchParams?.RequirementType === "VISA"
    ) {
      setInitialValues(InquiryVisaDetails);
    } else if (
      RequirementType === "HOTEL" ||
      searchParams?.RequirementType === "HOTEL"
    ) {
      setInitialValues(InquiryHotelDetails);
    } else {
      setInitialValues({
        ...InquiryDetails,
        // ExpClosureDate: InquiryDetails?.ExpClosureDate
        //   ? moment(InquiryDetails?.ExpClosureDate, "YYYY-MM-DD")?.toDate()
        //   : "",
        TravelDate: InquiryDetails?.TravelDate
          ? moment(InquiryDetails?.TravelDate, "YYYY-MM-DD")?.toDate()
          : "",
        DepartureDate: InquiryDetails?.DepartureDate
          ? moment(InquiryDetails?.DepartureDate, "YYYY-MM-DD")?.toDate()
          : "",
        ReturnDate: InquiryDetails?.ReturnDate
          ? moment(InquiryDetails?.ReturnDate, "YYYY-MM-DD")?.toDate()
          : "",
        Adult: InquiryDetails?.Adult ? `${InquiryDetails?.Adult}` : null,
        Child:
          InquiryDetails?.Child !== 0 ? `${InquiryDetails?.Child}` : "zero",
        Infant:
          InquiryDetails?.Infant !== 0 ? `${InquiryDetails?.Infant}` : "zero",
        PreferredAirlineID: InquiryDetails?.PreferredAirlineID
          ? InquiryDetails?.PreferredAirlineID?.split(", ")?.map(
              (flight: any) => +flight,
            )
          : "",
        DestinationID: InquiryDetails?.DestinationID
          ? +InquiryDetails?.DestinationID
          : null,
        HotelPreference: InquiryDetails?.HotelPreference
          ? InquiryDetails?.HotelPreference?.split(",")?.map((pref: any) =>
              pref?.trim(),
            )
          : "",
      });
    }
  }, [
    InquiryPackageDetails,
    InquiryFlightDetails,
    InquiryHotelDetails,
    InquiryVisaDetails,
    InquiryDetails,
  ]);

  useEffect(() => {
    if (LeadDetails?.ID) {
      setInitialValues({
        ...initialValues,
        Initial: LeadDetails?.Initial,
        FirstName: LeadDetails?.FirstName,
        LastName: LeadDetails?.LastName,
        MobileNo: LeadDetails?.MobileNo,
        EmailID: LeadDetails?.EmailID,
        TypeofInquiry: LeadDetails?.LeadType,
        InquirySourceID: LeadDetails?.LeadSourceID,
        ContactID: LeadDetails?.ContactID,
        DestinationID: LeadDetails?.SectorID,
        LeadID: LeadDetails?.ID,
      });
    }
    return () => {
      dispatch(clearLeadDetails());
    };
  }, [LeadDetails]);

  useEffect(() => {
    if (ContactDetails?.ID) {
      setInitialValues({
        ...initialValues,
        Initial: ContactDetails?.Initial,
        FirstName: ContactDetails?.FirstName,
        LastName: ContactDetails?.LastName,
        MobileNo: ContactDetails?.MobileNo,
        EmailID: ContactDetails?.EmailID,
        TypeofInquiry: ContactDetails?.TypeofInquiry,
        InquirySourceID: ContactDetails?.LeadSourceID,
        ContactID: ContactDetails?.ID,
      });
    }
    return () => {
      dispatch(clearContactDetails());
    };
  }, [ContactDetails]);

  useEffect(() => {
    if (InquiryID) {
      if (RequirementType === "PACKAGE") {
        dispatch(getPackageInquiryByID(InquiryID));
      } else if (RequirementType === "FLIGHT") {
        dispatch(getFlightInquiryByID(InquiryID));
      } else if (RequirementType === "VISA") {
        dispatch(getVisaInquiryByID(InquiryID));
      } else if (RequirementType === "HOTEL") {
        dispatch(getHotelInquiryByID(InquiryID));
      } else {
        dispatch(getInquiryByID(InquiryID));
      }
    } else if (LeadID) {
      dispatch(getLeadByID(LeadID));
    } else if (ContactID) {
      dispatch(getContactByID(ContactID));
    }
  }, [InquiryID]);

  useEffect(() => {
    if (searchParams?.InquiryID) {
      if (searchParams?.RequirementType === "PACKAGE") {
        dispatch(getPackageInquiryByID(+searchParams?.InquiryID));
      } else if (searchParams?.RequirementType === "FLIGHT") {
        dispatch(getFlightInquiryByID(+searchParams?.InquiryID));
      } else if (searchParams?.RequirementType === "VISA") {
        dispatch(getVisaInquiryByID(+searchParams?.InquiryID));
      } else if (searchParams?.RequirementType === "HOTEL") {
        dispatch(getHotelInquiryByID(+searchParams?.InquiryID));
      } else {
        dispatch(getInquiryByID(+searchParams?.InquiryID));
      }
    }
  }, [searchParams?.InquiryID]);

  useEffect(() => {
    dispatch(getAllActiveCities());
    dispatch(getAllActiveVisaCountries());
    dispatch(getAllActiveVisaCategories());
    dispatch(getAllActiveNationalities());
    dispatch(getAllActiveFoodOptions());
    dispatch(getAllActiveMealTypes());
    dispatch(getAllActiveSectors());
    dispatch(getAllActiveAirlines());
    dispatch(getAllActiveAirlineClass());
    dispatch(getAllActiveLeadSource());
    dispatch(getAllActiveEntryTypes());

    return () => {
      dispatch(clearInquiryDetails());
    };
  }, []);

  const handleHotelSearchChange = React.useCallback(
    async (search: string, field: string, formRenderProps: FormRenderProps) => {
      const result = await dispatch(getAllFlightPlaceBySearch(search));
      if (result.payload) {
        formRenderProps.onChange(field, {
          value: result.payload,
        });
      }
    },
    [dispatch],
  );

  const handleTravelerChange = (
    type: "ADULT" | "CHILD" | "INFANT",
    value: number | string,
    formRenderProps: FormRenderProps,
  ) => {
    const currentAdult = Number(formRenderProps.valueGetter("Adult") || 0) || 0;
    const currentChild = Number(formRenderProps.valueGetter("Child") || 0) || 0;
    const currentInfant =
      Number(formRenderProps.valueGetter("Infant") || 0) || 0;
    const updatedValue = value === "zero" ? 0 : value;

    const updatedCounts = {
      ADULT: type === "ADULT" ? updatedValue : currentAdult,
      CHILD: type === "CHILD" ? updatedValue : currentChild,
      INFANT: type === "INFANT" ? updatedValue : currentInfant,
    };

    const totalTravelers =
      Number(updatedCounts.ADULT) +
      Number(updatedCounts.CHILD) +
      Number(updatedCounts.INFANT);

    if (type === "ADULT" && updatedCounts.INFANT > value) {
      formRenderProps.onChange("Adult", { value: `${updatedCounts.INFANT}` });
      ErrorToast("Number of Adults cannot be less than Infants");
    } else if (type === "INFANT" && updatedCounts.ADULT < value) {
      ErrorToast("Number of Infants cannot be more than Adults");
      formRenderProps.onChange("Infant", { value: `zero` });
    } else if (totalTravelers > 9) {
      ErrorToast("Total number of travellers cannot exceed 9");
      formRenderProps.onChange(
        type === "ADULT" ? "Adult" : type === "CHILD" ? "Child" : "Infant",
        {
          value: `${type === "ADULT" ? "1" : "zero"}`,
        },
      );
    } else {
      formRenderProps.onChange(
        type === "ADULT" ? "Adult" : type === "CHILD" ? "Child" : "Infant",
        { value: `${value}` },
      );
    }
  };

  const handleSubmit = async (values: any) => {
    let contactID = values?.ContactID ? +values?.ContactID : null;
    const insertContactPayload = {
      Initial: values?.Initial ? values?.Initial : "",
      FirstName: values?.FirstName ? values?.FirstName : "",
      LastName: values?.LastName ? values?.LastName : "",
      MobileNo: values?.MobileNo ? values?.MobileNo : "",
      CountryCode: values?.CountryCode ? values?.CountryCode : "",
      EmailID: values?.EmailID ? values?.EmailID : "",
      ContactType: values?.TypeofInquiry ? values?.TypeofInquiry : "",
      LeadSourceID: values?.InquirySourceID ? +values?.InquirySourceID : null,
    };

    if (!LeadID && !InquiryID && !ContactID) {
      const response = await dispatch(
        insertContactFromOtherPlace(insertContactPayload),
      );

      if (
        response.meta.requestStatus === "fulfilled" &&
        response.payload?.Data?.ID
      ) {
        contactID = +response.payload.Data.ID || null;
      } else {
        return;
      }
    }

    if (values?.RequirementType === "PACKAGE") {
      if (InquiryID) {
        const editPayload = {
          ID: InquiryID ? +InquiryID : null,
          RequirementType: values?.RequirementType
            ? values?.RequirementType
            : "",
          Initial: values?.Initial ? values?.Initial : "",
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? `${values?.MobileNo}` : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          TypeofInquiry: values?.TypeofInquiry ? values?.TypeofInquiry : "",
          Goingfrom: values?.Goingfrom ? values?.Goingfrom : "",
          DestinationID: values?.DestinationID ? +values?.DestinationID : null,
          // GoingfromID: values?.GoingfromID ? +values?.GoingfromID : null,
          FoodPreferenceID: values?.FoodPreferenceID
            ? +values?.FoodPreferenceID
            : null,
          TravelDate: values?.TravelDate
            ? moment(values?.TravelDate)?.format("YYYY-MM-DD")
            : null,
          NoOfNights: values?.NoOfNights ? +values?.NoOfNights : null,
          NoOfPax: values?.NoOfPax ? +values?.NoOfPax : null,
          Budget: values?.Budget ? +values?.Budget : null,
          InquirySourceID: values?.InquirySourceID
            ? +values?.InquirySourceID
            : null,
          ExpClosureDate: null,
          ExpClosureAmount: null,
          // ExpClosureDate: values?.ExpClosureDate
          //   ? moment(values?.ExpClosureDate)?.format("YYYY-MM-DD")
          //   : null,
          // ExpClosureAmount: values?.ExpClosureAmount
          //   ? +values?.ExpClosureAmount
          //   : null,
          HotelPreference: values?.HotelPreference
            ? values?.HotelPreference?.join(", ")
            : "",
          QueryType: values?.QueryType ? values?.QueryType : "",
          Remarks: values?.Remarks ? values?.Remarks : "",
          City: values?.City ? values?.City : "",
          Travelers: values.Travelers,
          HotelRooms: values?.HotelRooms ? values?.HotelRooms : [],
        };
        try {
          const response = await dispatch(updatePackageInquiry(editPayload));
          if (response?.meta?.requestStatus === "fulfilled") {
            dispatch(clearInquiryDetails());
            navigate("/inquiry");
            // navigate(-1);
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      } else {
        const insertPayload = {
          ContactID: contactID,
          LeadID: values?.LeadID ? +values?.LeadID : null,
          RequirementType: values?.RequirementType
            ? values?.RequirementType
            : "",
          Initial: values?.Initial ? values?.Initial : "",
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? `${values?.MobileNo}` : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          TypeofInquiry: values?.TypeofInquiry ? values?.TypeofInquiry : "",
          Goingfrom: values?.Goingfrom ? values?.Goingfrom : "",
          DestinationID: values?.DestinationID ? +values?.DestinationID : null,
          // GoingfromID: values?.GoingfromID ? +values?.GoingfromID : null,
          FoodPreferenceID: values?.FoodPreferenceID
            ? +values?.FoodPreferenceID
            : null,
          TravelDate: values?.TravelDate
            ? moment(values?.TravelDate)?.format("YYYY-MM-DD")
            : null,
          NoOfNights: values?.NoOfNights ? +values?.NoOfNights : null,
          NoOfPax: values?.NoOfPax ? +values?.NoOfPax : null,
          Budget: values?.Budget ? +values?.Budget : null,
          InquirySourceID: values?.InquirySourceID
            ? +values?.InquirySourceID
            : null,
          ExpClosureDate: null,
          ExpClosureAmount: null,
          // ExpClosureDate: values?.ExpClosureDate
          //   ? moment(values?.ExpClosureDate)?.format("YYYY-MM-DD")
          //   : null,
          // ExpClosureAmount: values?.ExpClosureAmount
          //   ? +values?.ExpClosureAmount
          //   : null,
          HotelPreference: values?.HotelPreference
            ? values?.HotelPreference?.join(", ")
            : "",
          QueryType: values?.QueryType ? values?.QueryType : "",
          Remarks: values?.Remarks ? values?.Remarks : "",
          Travelers: values.Travelers,
          HotelRooms: values?.HotelRooms ? values?.HotelRooms : [],
        };
        try {
          const response = await dispatch(createPackageInquiry(insertPayload));
          if (response?.meta?.requestStatus === "fulfilled") {
            dispatch(clearInquiryDetails());
            navigate("/inquiry");
            // navigate(-1);
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      }
    } else if (values?.RequirementType === "FLIGHT") {
      if (InquiryID) {
        const editPayload = {
          ID: InquiryID ? +InquiryID : null,
          RequirementType: values?.RequirementType
            ? values?.RequirementType
            : "",
          Initial: values?.Initial ? values?.Initial : "",
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? `${values?.MobileNo}` : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          TypeofInquiry: values?.TypeofInquiry ? values?.TypeofInquiry : "",
          TripType: values?.TripType ? values?.TripType : "",
          FromPlaceID: values?.FromPlaceID ? +values?.FromPlaceID : null,
          ToPlaceID: values?.ToPlaceID ? +values?.ToPlaceID : null,
          DepartureDate: values?.DepartureDate
            ? moment(values?.DepartureDate)?.format("YYYY-MM-DD")
            : null,
          ReturnDate: values?.ReturnDate
            ? moment(values?.ReturnDate)?.format("YYYY-MM-DD")
            : null,
          Adult: values?.Adult ? +values?.Adult : 0,
          Child: values?.Child
            ? values?.Child !== "zero"
              ? +values?.Child
              : 0
            : 0,
          Infant: values?.Infant
            ? values?.Infant !== "zero"
              ? +values?.Infant
              : 0
            : 0,
          FlightClassID: values?.FlightClassID ? +values?.FlightClassID : null,
          PreferredAirlineID: values?.PreferredAirlineID
            ? values?.PreferredAirlineID?.join(", ")
            : "",
          InquirySourceID: values?.InquirySourceID
            ? +values?.InquirySourceID
            : null,
          ExpClosureDate: null,
          ExpClosureAmount: null,
          // ExpClosureDate: values?.ExpClosureDate
          //   ? moment(values?.ExpClosureDate)?.format("YYYY-MM-DD")
          //   : null,
          // ExpClosureAmount: values?.ExpClosureAmount
          //   ? +values?.ExpClosureAmount
          //   : null,
          Remarks: values?.Remarks ? values?.Remarks : "",
        };
        try {
          const response = await dispatch(updateFlightInquiry(editPayload));
          if (response?.meta?.requestStatus === "fulfilled") {
            dispatch(clearInquiryDetails());
            navigate("/inquiry");
            // navigate(-1);
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      } else {
        const insertPayload = {
          ContactID: contactID,
          LeadID: values?.LeadID ? +values?.LeadID : null,
          RequirementType: values?.RequirementType
            ? values?.RequirementType
            : "",
          Initial: values?.Initial ? values?.Initial : "",
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? `${values?.MobileNo}` : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          TypeofInquiry: values?.TypeofInquiry ? values?.TypeofInquiry : "",
          TripType: values?.TripType ? values?.TripType : "",
          FromPlaceID: values?.FromPlaceID ? +values?.FromPlaceID : null,
          ToPlaceID: values?.ToPlaceID ? +values?.ToPlaceID : null,
          DepartureDate: values?.DepartureDate
            ? moment(values?.DepartureDate)?.format("YYYY-MM-DD")
            : null,
          ReturnDate: values?.ReturnDate
            ? moment(values?.ReturnDate)?.format("YYYY-MM-DD")
            : null,
          Adult: values?.Adult ? +values?.Adult : 0,
          Child: values?.Child
            ? values?.Child !== "zero"
              ? +values?.Child
              : 0
            : 0,
          Infant: values?.Infant
            ? values?.Infant !== "zero"
              ? +values?.Infant
              : 0
            : 0,
          FlightClassID: values?.FlightClassID ? +values?.FlightClassID : null,
          PreferredAirlineID: values?.PreferredAirlineID
            ? values?.PreferredAirlineID?.join(", ")
            : "",
          InquirySourceID: values?.InquirySourceID
            ? +values?.InquirySourceID
            : null,
          ExpClosureDate: null,
          ExpClosureAmount: null,
          // ExpClosureDate: values?.ExpClosureDate
          //   ? moment(values?.ExpClosureDate)?.format("YYYY-MM-DD")
          //   : null,
          // ExpClosureAmount: values?.ExpClosureAmount
          //   ? +values?.ExpClosureAmount
          //   : null,
          Remarks: values?.Remarks ? values?.Remarks : "",
        };
        try {
          const response = await dispatch(createFlightInquiry(insertPayload));
          if (response?.meta?.requestStatus === "fulfilled") {
            dispatch(clearInquiryDetails());
            navigate("/inquiry");
            // navigate(-1);
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      }
    } else if (values?.RequirementType === "VISA") {
      if (InquiryID) {
        const editPayload = {
          ID: InquiryID ? +InquiryID : null,
          RequirementType: values?.RequirementType
            ? values?.RequirementType
            : "",
          InquirySourceID: values?.InquirySourceID
            ? +values?.InquirySourceID
            : null,
          Remarks: values?.Remarks ? values?.Remarks : "",
          CountryID: values?.CountryID ? +values?.CountryID : null,
          VisaCategoryID: values?.VisaCategoryID
            ? +values?.VisaCategoryID
            : null,
          EntryTypeID: values?.EntryTypeID ? values?.EntryTypeID : "",
          DateofTravel: values.DateofTravel
            ? moment(values.DateofTravel).format("YYYY-MM-DD")
            : null,
          Adult: values?.Adult ? +values?.Adult : 0,
          Child: values?.Child
            ? values?.Child !== "zero"
              ? +values?.Child
              : 0
            : 0,
          Infant: values?.Infant
            ? values?.Infant !== "zero"
              ? +values?.Infant
              : 0
            : 0,
          Duration: values?.Duration ? +values?.Duration : 0,
          NationalityID: values?.NationalityID ? +values?.NationalityID : null,
          Initial: values?.Initial ? values?.Initial : "",
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? `${values?.MobileNo}` : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          TypeofInquiry: values?.TypeofInquiry ? values?.TypeofInquiry : "",
        };
        try {
          const response = await dispatch(updateVisaInquiry(editPayload));
          if (response?.meta?.requestStatus === "fulfilled") {
            dispatch(clearInquiryDetails());
            navigate("/inquiry");
            // navigate(-1);
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      } else {
        const insertPayload = {
          ContactID: contactID,
          LeadID: values?.LeadID ? +values?.LeadID : null,
          RequirementType: values?.RequirementType
            ? values?.RequirementType
            : "",
          InquirySourceID: values?.InquirySourceID
            ? +values?.InquirySourceID
            : null,
          Remarks: values?.Remarks ? values?.Remarks : "",
          CountryID: values?.CountryID ? +values?.CountryID : null,
          VisaCategoryID: values?.VisaCategoryID
            ? +values?.VisaCategoryID
            : null,
          EntryTypeID: values?.EntryTypeID ? values?.EntryTypeID : "",
          DateofTravel: values.DateofTravel
            ? moment(values.DateofTravel).format("YYYY-MM-DD")
            : null,
          Adult: values?.Adult ? +values?.Adult : 0,
          Child: values?.Child
            ? values?.Child !== "zero"
              ? +values?.Child
              : 0
            : 0,
          Infant: values?.Infant
            ? values?.Infant !== "zero"
              ? +values?.Infant
              : 0
            : 0,
          Duration: values?.Duration ? +values?.Duration : 0,
          NationalityID: values?.NationalityID ? +values?.NationalityID : null,
          Initial: values?.Initial ? values?.Initial : "",
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? `${values?.MobileNo}` : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          TypeofInquiry: values?.TypeofInquiry ? values?.TypeofInquiry : "",
        };
        try {
          const response = await dispatch(createVisaInquiry(insertPayload));
          if (response?.meta?.requestStatus === "fulfilled") {
            dispatch(clearInquiryDetails());
            navigate("/inquiry");
            // navigate(-1);
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      }
    } else if (values?.RequirementType === "HOTEL") {
      if (InquiryID) {
        const editPayload = {
          ID: InquiryID ? +InquiryID : null,
          RequirementType: values?.RequirementType
            ? values?.RequirementType
            : "",
          InquirySourceID: values?.InquirySourceID
            ? +values?.InquirySourceID
            : null,
          Remarks: values?.Remarks ? values?.Remarks : "",
          NationalityID: values?.NationalityID ? +values?.NationalityID : null,
          Initial: values?.Initial ? values?.Initial : "",
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? `${values?.MobileNo}` : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          TypeofInquiry: values?.TypeofInquiry ? values?.TypeofInquiry : "",
          CityID: values?.CityID ? +values?.CityID : null,
          CheckIn: values.CheckIn
            ? moment(values.CheckIn).format("YYYY-MM-DD")
            : null,
          CheckOut: values.CheckOut
            ? moment(values.CheckOut).format("YYYY-MM-DD")
            : null,
          NoOfNights: values?.NoOfNights ? +values?.NoOfNights : null,
          Travelers: values.Travelers,
          StarRating: values.StarRating,
          MealTypeID: values?.MealTypeID ? +values?.MealTypeID : null,
          HotelRooms: values?.HotelRooms ? values?.HotelRooms : [],
        };
        try {
          const response = await dispatch(updateHotelInquiry(editPayload));
          if (response?.meta?.requestStatus === "fulfilled") {
            dispatch(clearInquiryDetails());
            navigate("/inquiry");
            // navigate(-1);
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      } else {
        const insertPayload = {
          ContactID: contactID,
          LeadID: values?.LeadID ? +values?.LeadID : null,
          ID: InquiryID ? +InquiryID : null,
          RequirementType: values?.RequirementType
            ? values?.RequirementType
            : "",
          InquirySourceID: values?.InquirySourceID
            ? +values?.InquirySourceID
            : null,
          Remarks: values?.Remarks ? values?.Remarks : "",
          NationalityID: values?.NationalityID ? +values?.NationalityID : null,
          Initial: values?.Initial ? values?.Initial : "",
          FirstName: values?.FirstName ? values?.FirstName : "",
          LastName: values?.LastName ? values?.LastName : "",
          MobileNo: values?.MobileNo ? `${values?.MobileNo}` : "",
          EmailID: values?.EmailID ? values?.EmailID : "",
          TypeofInquiry: values?.TypeofInquiry ? values?.TypeofInquiry : "",
          CityID: values?.CityID ? +values?.CityID : null,
          CheckIn: values.CheckIn
            ? moment(values.CheckIn).format("YYYY-MM-DD")
            : null,
          CheckOut: values.CheckOut
            ? moment(values.CheckOut).format("YYYY-MM-DD")
            : null,
          NoOfNights: values?.NoOfNights ? +values?.NoOfNights : null,
          Travelers: values.Travelers,
          StarRating: values.StarRating,
          MealTypeID: values?.MealTypeID ? +values?.MealTypeID : null,
          HotelRooms: values?.HotelRooms ? values?.HotelRooms : [],
        };
        try {
          const response = await dispatch(createHotelInquiry(insertPayload));
          if (response?.meta?.requestStatus === "fulfilled") {
            dispatch(clearInquiryDetails());
            navigate("/inquiry");
            // navigate(-1);
          }
        } catch (error) {
          console.error("Error in handleSubmit:", error);
          throw error;
        }
      }
    }
  };

  return (
    <>
      {(loading || leadLoading || contactLoading) && (
        <LoadingPanel gridRef={gridRef} />
      )}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12, marginBottom: 10 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4 className="typography-head">
                    {InquiryID ? "Update Inquiry" : "Add Inquiry"}
                  </Typography.h4>
                </GridLayoutItem>
                {LeadID || InquiryID || ContactID ? (
                  <GridLayoutItem style={{ width: "100%" }} colSpan={3}>
                    <table className="w-100">
                      <tr>
                        <th>Name</th>
                        <td>:</td>
                        <td>
                          {initialValues?.Initial} {initialValues?.FirstName}{" "}
                          {initialValues?.LastName}
                        </td>
                      </tr>
                      <tr>
                        <th>Mobile No.</th>
                        <td>:</td>
                        <td>{initialValues?.MobileNo}</td>
                        <th>Email ID</th>
                        <td>:</td>
                        <td>{initialValues?.EmailID}</td>
                      </tr>
                    </table>
                    <hr className="mt-4 mb-4" />
                  </GridLayoutItem>
                ) : (
                  <>
                    <GridLayoutItem className="d-flex gap-2">
                      <Field
                        wrapperStyle={{
                          width: "100%",
                          flex: 1,
                        }}
                        id={"Initial"}
                        name={"Initial"}
                        label={"Initial"}
                        placeholder="i.e. Mr."
                        component={FormSelectionField}
                        options={NAMEPREFIX?.map((initial: any) => {
                          return {
                            value: initial?.value,
                            label: initial?.label,
                          };
                        })}
                      />
                      <Field
                        wrapperStyle={{
                          width: "100%",
                          flex: 2,
                        }}
                        name="FirstName"
                        label="First Name"
                        placeholder="i.e.John"
                        maxLength="100"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="LastName"
                        label="Last Name"
                        placeholder="i.e. Smith"
                        maxLength="100"
                        component={FormTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name="EmailID"
                        label="Email ID"
                        astrike={true}
                        placeholder="i.e. example@gmail.com"
                        component={FormTextField}
                        validator={emailValidator}
                      />
                    </GridLayoutItem>
                    {/* <GridLayoutItem className="mb-4">
                      <Field
                        wrapperStyle={{
                          width: "100%",
                          flex: 1,
                        }}
                        name="CountryCode"
                        label="Code"
                        astrike={true}
                        placeholder="i.e. In"
                        component={CountryCodeField}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem> */}
                    <GridLayoutItem className="mb-4">
                      <Field
                        // wrapperStyle={{
                        //   width: "100%",
                        //   flex: 2,
                        // }}
                        name="MobileNo"
                        label="Mobile No."
                        // type="number"
                        // max={16}
                        type="text"
                        maxLength={16}
                        onKeyPress={(e: any) => {
                          const allowedChars = /^[0-9+]$/;
                          if (!allowedChars.test(e?.key)) {
                            e.preventDefault();
                          }
                        }}
                        placeholder="i.e. 9999999999"
                        component={FormTextField}
                        astrike={true}
                        validator={MobileNoIntValidator}
                      />
                    </GridLayoutItem>
                    {/* <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        id={"LeadSourceID"}
                        name={"LeadSourceID"}
                        label={"Lead Source"}
                        placeholder="Lead Source"
                        component={FormSelectionField}
                        astrike={true}
                        validator={requiredValidator}
                        options={LeadSourceList?.map(
                          (leadsource: ILeadSource) => {
                            return {
                              value: leadsource?.ID,
                              label: leadsource?.Source,
                            };
                          }
                        )}
                      />
                    </GridLayoutItem> */}
                  </>
                )}
                <GridLayoutItem colSpan={3}>
                  <div
                    style={{
                      background: "#edeaea",
                      borderRadius: 10,
                      padding: "1px 10px 15px",
                      fontWeight: 600,
                    }}
                  >
                    <Field
                      // wrapperStyle={{
                      //   marginTop: 8,
                      // }}
                      label={"Requirement Type :"}
                      id={"RequirementType"}
                      name={"RequirementType"}
                      component={FormRadioGroup}
                      layout="horizontal"
                      options={INQUIRYQUERIES}
                      validator={requiredValidator}
                      wrapperClassName="d-flex align-items-center gap-4"
                      onChange={(e) => {
                        formRenderProps.onChange("RequirementType", {
                          value: e.value,
                        });
                        formRenderProps?.onChange("HotelRooms", {
                          value: [
                            {
                              Adults: 2,
                              Child: 0,
                              Infant: 0,
                            },
                          ],
                        });
                        formRenderProps.onChange("Travelers", {
                          value: 2,
                        });
                      }}
                    />
                  </div>
                </GridLayoutItem>
                {formRenderProps.valueGetter("RequirementType") ===
                  "PACKAGE" && (
                  <>
                    <GridLayoutItem>
                      <Field
                        wrapperStyle={{
                          marginTop: 8,
                        }}
                        id={"QueryType"}
                        name={"QueryType"}
                        label={"Query Type"}
                        component={FormRadioGroup}
                        layout="horizontal"
                        options={QUERYTYPES}
                        validator={requiredValidator}
                        // astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={2}>
                      <Field
                        name={"TypeofInquiry"}
                        label={"Inquiry Type"}
                        wrapperClassName="w-50"
                        placeholder="i.e. B2B"
                        component={FormSelectionField}
                        options={INQUIRYTYPE?.map((initial: any) => {
                          return {
                            value: initial?.value,
                            label: initial?.label,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        id={"DestinationID"}
                        name={"DestinationID"}
                        label={"Going To"}
                        placeholder="Going To"
                        component={FormSelectionField}
                        astrike={true}
                        validator={requiredValidator}
                        options={SectorList?.map((sector: ISector) => {
                          return {
                            value: sector?.ID,
                            label: sector?.SectorName,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      {/* <Field
                        wrapperClassName="w-100"
                        id={"GoingfromID"}
                        name={"GoingfromID"}
                        label={"Going From"}
                        placeholder="Going From"
                        component={FormSelectionField}
                        astrike={true}
                        validator={requiredValidator}
                        options={CityList?.map((city: ICity) => {
                          return {
                            value: city?.ID,
                            label: city?.CityName,
                          };
                        })}
                      /> */}
                      <Field
                        // wrapperClassName="right-aligned-field"
                        name="Goingfrom"
                        label="Pickup Place"
                        placeholder="i.e. Ahmedabad"
                        component={FormTextField}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={"TravelDate"}
                        label="Travel Date"
                        format="dd/MM/yyyy"
                        astrike={true}
                        component={FormDatePicker}
                        validator={requiredValidator}
                        minDate={moment().toDate()}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="right-aligned-field"
                        name="NoOfNights"
                        label="No Of Nights"
                        placeholder="0"
                        max={50}
                        format="n0"
                        component={FormNumericTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      style={{
                        marginTop: 15,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Label>
                        {"Travellers"}
                        <span style={{ color: "red" }}>*</span>
                      </Label>
                      <Button
                        type="button"
                        style={{ background: "#fff" }}
                        onClick={() =>
                          dispatch(openDialog("packageTravellerEditDialog"))
                        }
                      >
                        {formRenderProps.valueGetter("Travelers") || 0}
                      </Button>
                    </GridLayoutItem>
                    <TravellerEditDialog formRenderProps={formRenderProps} />
                    {/* <GridLayoutItem>
                      <Field
                        wrapperClassName="right-aligned-field"
                        name="NoOfPax"
                        label="No Of Pax"
                        placeholder="0"
                        max={50}
                        format="n0"
                        component={FormNumericTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem> */}
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="right-aligned-field"
                        name="Budget"
                        label="Budget"
                        placeholder="0"
                        component={FormNumericTextField}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        id={"InquirySourceID"}
                        name={"InquirySourceID"}
                        label={"Lead Source"}
                        placeholder="Lead Source"
                        isAddNew={true}
                        isIndirect={true}
                        drawerName="leadsourceForm"
                        component={FormSelectionField}
                        astrike={true}
                        validator={requiredValidator}
                        options={LeadSourceList?.map(
                          (leadsource: ILeadSource) => {
                            return {
                              value: leadsource?.ID,
                              label: leadsource?.Source,
                            };
                          },
                        )}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        id={"FoodPreferenceID"}
                        name={"FoodPreferenceID"}
                        label={"Food Preference"}
                        placeholder="i.e. Regular"
                        component={FormSelectionField}
                        isAddNew={true}
                        isIndirect={true}
                        drawerName="foodoptionForm"
                        options={FoodOptionList?.map(
                          (foodoption: IFoodOption) => {
                            return {
                              value: foodoption?.ID,
                              label: foodoption?.Name,
                            };
                          },
                        )}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        id={"HotelPreference"}
                        name={"HotelPreference"}
                        title={"Hotel Preference"}
                        marginRight={20}
                        marginBeetween={5}
                        component={FormCheckboxGroup}
                        options={HOTELPREFERENCE}
                      />
                    </GridLayoutItem>
                    {/* <GridLayoutItem>
                      <Field
                        name={"ExpClosureDate"}
                        label="Expected Closure Date"
                        format="dd/MM/yyyy"
                        astrike={true}
                        component={FormDatePicker}
                        validator={requiredValidator}
                        minDate={moment().toDate()}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="right-aligned-field"
                        name="ExpClosureAmount"
                        label="Expected Closure Amount"
                        placeholder="0"
                        format="n0"
                        component={FormNumericTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem> */}
                    <GridLayoutItem colSpan={3}>
                      <Field
                        name="Remarks"
                        label="Remarks"
                        placeholder="Remarks"
                        component={FormTextField}
                      />
                    </GridLayoutItem>
                  </>
                )}

                {formRenderProps.valueGetter("RequirementType") ===
                  "FLIGHT" && (
                  <>
                    <GridLayoutItem>
                      <Field
                        wrapperStyle={{
                          marginTop: 8,
                        }}
                        id={"TripType"}
                        name={"TripType"}
                        label={"Trip Type"}
                        component={FormRadioGroup}
                        layout="horizontal"
                        options={TRIPTYPE}
                        validator={requiredValidator}
                        // astrike={true}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={2}>
                      <Field
                        name={"TypeofInquiry"}
                        label={"Inquiry Type"}
                        placeholder="i.e. B2B"
                        wrapperClassName="w-50"
                        component={FormSelectionField}
                        options={INQUIRYTYPE?.map((initial: any) => {
                          return {
                            value: initial?.value,
                            label: initial?.label,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        id={"FromPlaceID"}
                        name={"FromPlaceID"}
                        label={"Source City"}
                        placeholder="Source City"
                        component={FormIncrementalSearch}
                        astrike={true}
                        validator={requiredValidator}
                        // isAddNew={true}
                        // isIndirect={true}
                        // drawerName="cityForm"
                        fetchIncrementalData={(search: any) =>
                          handleHotelSearchChange(
                            search,
                            "from_place_options",
                            formRenderProps,
                          )
                        }
                        // options={CityList?.map((city: ICity) => {
                        //   return {
                        //     value: city?.ID,
                        //     label: city?.CityName,
                        //   };
                        // })}
                        options={
                          formRenderProps
                            .valueGetter("from_place_options")
                            ?.map((item: any) => {
                              return {
                                value: item?.ID,
                                label: item?.FlightPlaceName,
                              };
                            }) || []
                        }
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        id={"ToPlaceID"}
                        name={"ToPlaceID"}
                        label={"Destination City"}
                        placeholder="Destination City"
                        component={FormIncrementalSearch}
                        astrike={true}
                        validator={requiredValidator}
                        fetchIncrementalData={(search: any) =>
                          handleHotelSearchChange(
                            search,
                            "to_place_options",
                            formRenderProps,
                          )
                        }
                        options={
                          formRenderProps
                            .valueGetter("to_place_options")
                            ?.map((item: any) => {
                              return {
                                value: item?.ID,
                                label: item?.FlightPlaceName,
                              };
                            }) || []
                        }
                        // isAddNew={true}
                        // isIndirect={true}
                        // drawerName="cityForm"

                        // options={CityList?.map((city: ICity) => {
                        //   return {
                        //     value: city?.ID,
                        //     label: city?.CityName,
                        //   };
                        // })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={"DepartureDate"}
                        label="Departure Date"
                        format="dd/MM/yyyy"
                        astrike={true}
                        component={FormDatePicker}
                        validator={requiredValidator}
                        minDate={moment().toDate()}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={"ReturnDate"}
                        label="Return Date"
                        format="dd/MM/yyyy"
                        astrike={
                          formRenderProps.valueGetter("TripType") === "One Way"
                            ? false
                            : true
                        }
                        minDate={moment(
                          formRenderProps.valueGetter("DepartureDate"),
                        ).toDate()}
                        onChange={(e) => {
                          if (e?.value) {
                            formRenderProps.onChange("TripType", {
                              value: "Two Way",
                            });
                          }
                        }}
                        component={FormDatePicker}
                        validator={
                          formRenderProps.valueGetter("TripType") === "One Way"
                            ? skipValidator
                            : requiredValidator
                        }
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        id={"Adult"}
                        name={"Adult"}
                        label={"Adult"}
                        placeholder="Adult"
                        astrike={true}
                        validator={requiredValidator}
                        component={FormSelectionField}
                        onChange={(event: any) =>
                          handleTravelerChange(
                            "ADULT",
                            event?.value === "zero"
                              ? "zero"
                              : Number(event?.value || 0),
                            formRenderProps,
                          )
                        }
                        options={PAXCOUNT?.map((count: any) => {
                          return {
                            value: count?.value,
                            label: count?.label,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        id={"Child"}
                        name={"Child"}
                        label={"Child"}
                        placeholder="Child"
                        astrike={true}
                        validator={requiredValidator}
                        component={FormSelectionField}
                        options={[
                          {
                            value: "zero",
                            label: "0",
                          },
                          ...(Array.isArray(PAXCOUNT)
                            ? PAXCOUNT.map((count: any) => ({
                                value: count.value,
                                label: count.label,
                              }))
                            : []),
                        ]}
                        onChange={(event: any) =>
                          handleTravelerChange(
                            "CHILD",
                            event?.value === "zero"
                              ? "zero"
                              : Number(event?.value || 0),
                            formRenderProps,
                          )
                        }
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        id={"Infant"}
                        name={"Infant"}
                        label={"Infant"}
                        placeholder="Infant"
                        astrike={true}
                        validator={requiredValidator}
                        component={FormSelectionField}
                        options={[
                          {
                            value: "zero",
                            label: "0",
                          },
                          ...(Array.isArray(PAXCOUNT)
                            ? PAXCOUNT.map((count: any) => ({
                                value: count.value,
                                label: count.label,
                              }))
                            : []),
                        ]}
                        onChange={(event: any) =>
                          handleTravelerChange(
                            "INFANT",
                            event?.value === "zero"
                              ? "zero"
                              : Number(event?.value || 0),
                            formRenderProps,
                          )
                        }
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        id={"FlightClassID"}
                        name={"FlightClassID"}
                        label={"Flight Class"}
                        placeholder="Flight Class"
                        component={FormSelectionField}
                        // astrike={true}
                        // validator={requiredValidator}
                        isAddNew={true}
                        isIndirect={true}
                        drawerName="airlineclassForm"
                        options={AirlineClassList?.map(
                          (flightclass: IAirlineClass) => {
                            return {
                              value: flightclass?.ID,
                              label: flightclass?.ClassName,
                            };
                          },
                        )}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        id={"PreferredAirlineID"}
                        name={"PreferredAirlineID"}
                        label={"Preferred Airline"}
                        placeholder="Preferred Airline"
                        component={FormMultiSelectionFiled}
                        // astrike={true}
                        // validator={requiredArrayValidator}
                        options={AirlineList?.map((airline: IAirline) => {
                          return {
                            value: airline?.ID,
                            label: airline?.AirlineName,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        id={"InquirySourceID"}
                        name={"InquirySourceID"}
                        label={"Lead Source"}
                        placeholder="Lead Source"
                        component={FormSelectionField}
                        astrike={true}
                        validator={requiredValidator}
                        isAddNew={true}
                        isIndirect={true}
                        drawerName="leadsourceForm"
                        options={LeadSourceList?.map(
                          (leadsource: ILeadSource) => {
                            return {
                              value: leadsource?.ID,
                              label: leadsource?.Source,
                            };
                          },
                        )}
                      />
                    </GridLayoutItem>
                    {/* <GridLayoutItem>
                      <Field
                        name={"ExpClosureDate"}
                        label="Expected Closure Date"
                        format="dd/MM/yyyy"
                        astrike={true}
                        component={FormDatePicker}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="right-aligned-field"
                        name="ExpClosureAmount"
                        label="Expected Closure Amount"
                        placeholder="0"
                        format="n0"
                        component={FormNumericTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                    </GridLayoutItem> */}
                    <GridLayoutItem colSpan={2}>
                      <Field
                        name="Remarks"
                        label="Remarks"
                        placeholder="Remarks"
                        component={FormTextField}
                      />
                    </GridLayoutItem>
                  </>
                )}

                {formRenderProps.valueGetter("RequirementType") === "VISA" && (
                  <>
                    <GridLayoutItem>
                      <Field
                        name={"TypeofInquiry"}
                        label={"Inquiry Type"}
                        placeholder="i.e. B2B"
                        component={FormSelectionField}
                        options={INQUIRYTYPE?.map((initial: any) => {
                          return {
                            value: initial?.value,
                            label: initial?.label,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        id={"CountryID"}
                        name={"CountryID"}
                        label={"Country Name"}
                        component={FormSelectionField}
                        placeholder="i.e india"
                        validator={requiredValidator}
                        astrike={true}
                        onChange={() =>
                          formRenderProps.onChange("VisaCategoryID", {
                            value: null,
                          })
                        }
                        options={VisaCountryList.map(
                          (country: IVisaCountry) => {
                            return {
                              value: country.ID,
                              label: country.VisaCountry,
                            };
                          },
                        )}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        id={"VisaCategoryID"}
                        name={"VisaCategoryID"}
                        label={"Visa Category"}
                        placeholder="Visa Category"
                        component={FormSelectionField}
                        astrike={true}
                        validator={requiredValidator}
                        isAddNew={true}
                        isIndirect={true}
                        drawerName="visacategoryForm"
                        options={VisaCategoryList?.filter(
                          (e) =>
                            e?.VisaCountryID ===
                            formRenderProps?.valueGetter("CountryID"),
                        )?.map((visacategory: IVisaCategory) => {
                          return {
                            value: visacategory?.ID,
                            label: visacategory?.VisaCategory,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <Field
                      id={"EntryTypeID"}
                      name={"EntryTypeID"}
                      label={"Entry Type"}
                      placeholder="Entry Type"
                      astrike={true}
                      validator={requiredValidator}
                      component={FormSelectionField}
                      options={EntryTypeList?.map((entryType: IEntryType) => {
                        return {
                          value: entryType?.ID,
                          label: entryType?.EntryType,
                        };
                      })}
                    />
                    <GridLayoutItem>
                      <Field
                        name={"DateofTravel"}
                        label="Date of Travel"
                        format="dd/MM/yyyy"
                        component={FormDatePicker}
                        // astrike={true}
                        // validator={requiredValidator}
                        minDate={moment().toDate()}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        id={"Adult"}
                        name={"Adult"}
                        label={"Adult"}
                        placeholder="Adult"
                        astrike={true}
                        validator={requiredValidator}
                        component={FormSelectionField}
                        options={PAXCOUNT?.map((count: any) => {
                          return {
                            value: count?.value,
                            label: count?.label,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        id={"Child"}
                        name={"Child"}
                        label={"Child"}
                        placeholder="Child"
                        // astrike={true}
                        // validator={requiredValidator}
                        component={FormSelectionField}
                        options={[
                          {
                            value: "zero",
                            label: "0",
                          },
                          ...(Array.isArray(PAXCOUNT)
                            ? PAXCOUNT.map((count: any) => ({
                                value: count.value,
                                label: count.label,
                              }))
                            : []),
                        ]}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        id={"Infant"}
                        name={"Infant"}
                        label={"Infant"}
                        placeholder="Infant"
                        // astrike={true}
                        // validator={requiredValidator}
                        component={FormSelectionField}
                        options={[
                          {
                            value: "zero",
                            label: "0",
                          },
                          ...(Array.isArray(PAXCOUNT)
                            ? PAXCOUNT.map((count: any) => ({
                                value: count.value,
                                label: count.label,
                              }))
                            : []),
                        ]}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="right-aligned-field"
                        name="Duration"
                        label="Duration (In Days)"
                        placeholder="0"
                        max={50}
                        format="n0"
                        component={FormNumericTextField}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={"NationalityID"}
                        label="Nationality"
                        placeholder="Nationality"
                        component={FormSelectionField}
                        options={NationalityList?.map(
                          (nationality: INationality) => {
                            return {
                              value: nationality?.ID,
                              label: nationality?.Nationality,
                            };
                          },
                        )}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={2}>
                      <Field
                        name="Remarks"
                        label="Remarks"
                        placeholder="Remarks"
                        component={FormTextField}
                      />
                    </GridLayoutItem>
                  </>
                )}
                {formRenderProps.valueGetter("RequirementType") === "HOTEL" && (
                  <>
                    <GridLayoutItem>
                      <Field
                        name={"TypeofInquiry"}
                        label={"Inquiry Type"}
                        placeholder="i.e. B2B"
                        component={FormSelectionField}
                        options={INQUIRYTYPE?.map((initial: any) => {
                          return {
                            value: initial?.value,
                            label: initial?.label,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        wrapperClassName="w-100"
                        id={"CityID"}
                        name={"CityID"}
                        label={"City"}
                        placeholder="City"
                        component={FormSelectionField}
                        astrike={true}
                        validator={requiredValidator}
                        options={CityList?.map((city: ICity) => {
                          return {
                            value: city?.ID,
                            label: city?.CityName,
                          };
                        })}
                      />
                    </GridLayoutItem>

                    <GridLayoutItem>
                      <Field
                        name={"CheckIn"}
                        label="Check In"
                        format="dd/MM/yyyy"
                        astrike={true}
                        component={FormDatePicker}
                        validator={requiredValidator}
                        minDate={moment().toDate()}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        name={"CheckOut"}
                        label="Check Out"
                        format="dd/MM/yyyy"
                        minDate={moment(
                          formRenderProps.valueGetter("CheckIn"),
                        ).toDate()}
                        component={FormDatePicker}
                        astrike={true}
                        validator={requiredValidator}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem className="d-flex gap-2">
                      <Field
                        wrapperClassName="right-aligned-field"
                        name="NoOfNights"
                        label="No Of Nights"
                        disabled
                        placeholder="0"
                        max={50}
                        format="n0"
                        component={FormNumericTextField}
                        validator={requiredValidator}
                        astrike={true}
                      />
                      <Field
                        name={"NationalityID"}
                        label="Nationality"
                        placeholder="Nationality"
                        component={FormSelectionField}
                        options={NationalityList?.map(
                          (nationality: INationality) => {
                            return {
                              value: nationality?.ID,
                              label: nationality?.Nationality,
                            };
                          },
                        )}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem
                      style={{
                        marginTop: 15,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Label>
                        {"Travellers"}
                        <span style={{ color: "red" }}>*</span>
                      </Label>
                      <Button
                        type="button"
                        style={{ background: "#fff" }}
                        onClick={() =>
                          dispatch(openDialog("travellerEditDialog"))
                        }
                      >
                        {formRenderProps.valueGetter("Travelers") || 0}
                      </Button>
                    </GridLayoutItem>
                    <TravellerEditDialog formRenderProps={formRenderProps} />
                    <GridLayoutItem>
                      <Field
                        name="StarRating"
                        component={FormRatingField}
                        label="Star Rating"
                      />
                    </GridLayoutItem>
                    <GridLayoutItem>
                      <Field
                        id={"MealTypeID"}
                        name={"MealTypeID"}
                        label={"Meal Type"}
                        placeholder="i.e. AP"
                        component={FormSelectionField}
                        isAddNew={true}
                        isIndirect={true}
                        drawerName="mealtypeForm"
                        options={MealTypeList?.map((mealtype: IMealType) => {
                          return {
                            value: mealtype?.ID,
                            label: mealtype?.MealType,
                          };
                        })}
                      />
                    </GridLayoutItem>
                    <GridLayoutItem colSpan={3}>
                      <Field
                        name="Remarks"
                        label="Remarks"
                        placeholder="Remarks"
                        component={FormTextField}
                      />
                    </GridLayoutItem>
                  </>
                )}
                <GridLayoutItem
                  colSpan={3}
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                    marginTop: 20,
                  }}
                >
                  <ButtonForAll
                    label={InquiryID ? "Update" : "Save"}
                    type="submit"
                    disabled={
                      !formRenderProps.allowSubmit || loading || contactLoading
                    }
                  />

                  <RippleButton
                    type="button"
                    fillMode="outline"
                    themeColor="primary"
                    style={{ marginLeft: 10 }}
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </RippleButton>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <CheckinCheckoutChangeWatcher formRenderProps={formRenderProps} />
          </FormElement>
        )}
      />
    </>
  );
};

interface TravellerEditDialogProps {
  formRenderProps: FormRenderProps;
}

const TravellerEditDialog: React.FC<TravellerEditDialogProps> = ({
  formRenderProps,
}) => {
  const dialogName = useAppSelector((state) => state.dialog.dialogName);

  if (dialogName === "travellerEditDialog") {
    return (
      <AppDialog>
        <GridLayout style={{ maxWidth: 600 }}>
          <FieldArray
            formRenderProps={formRenderProps}
            component={TravellerHotelRoomArray}
            name="HotelRooms"
          />
        </GridLayout>
      </AppDialog>
    );
  }

  if (dialogName === "packageTravellerEditDialog") {
    return (
      <AppDialog>
        <GridLayout style={{ maxWidth: 600 }}>
          <FieldArray
            formRenderProps={formRenderProps}
            component={TravellerHotelRoomArray}
            name="HotelRooms"
            isPackageInquiry={true}
          />
        </GridLayout>
      </AppDialog>
    );
  }

  return null;
};

export default CreateInquiry;
